/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CadoozProduct } from '../models/CadoozProduct';
import type { CadoozVoucher } from '../models/CadoozVoucher';
import type { CadoozVoucherArchiveInfoResponse } from '../models/CadoozVoucherArchiveInfoResponse';
import type { CadoozVoucherRequest } from '../models/CadoozVoucherRequest';
import type { EmployeeVoucherOrder } from '../models/EmployeeVoucherOrder';
import type { EmployeeVoucherOrderBulkCreate } from '../models/EmployeeVoucherOrderBulkCreate';
import type { EmployeeVoucherOrderBulkCreateRequest } from '../models/EmployeeVoucherOrderBulkCreateRequest';
import type { EmployeeVoucherOrderUpdate } from '../models/EmployeeVoucherOrderUpdate';
import type { EmployeeVoucherOrderUpdateRequest } from '../models/EmployeeVoucherOrderUpdateRequest';
import type { PaginatedCadoozProductList } from '../models/PaginatedCadoozProductList';
import type { PaginatedCadoozVoucherList } from '../models/PaginatedCadoozVoucherList';
import type { PaginatedEmployeeVoucherOrderList } from '../models/PaginatedEmployeeVoucherOrderList';
import type { PatchedCadoozVoucherRequest } from '../models/PatchedCadoozVoucherRequest';
import type { PatchedEmployeeVoucherOrderUpdateRequest } from '../models/PatchedEmployeeVoucherOrderUpdateRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class VoucherService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * @returns PaginatedCadoozProductList
     * @throws ApiError
     */
    public voucherCadoozProductsList({
        categories,
        limit,
        offset,
        voucherSelection,
    }: {
        /**
         * * `clothing_accessories` - clothing_accessories
         * * `car_mobility` - car_mobility
         * * `beauty_wellness` - beauty_wellness
         * * `books_music` - books_music
         * * `grocery` - grocery
         * * `hobby_travel` - hobby_travel
         * * `multimedia_electronics` - multimedia_electronics
         * * `toys_baby` - toys_baby
         * * `sports_lifestyle` - sports_lifestyle
         * * `streaming_gaming` - streaming_gaming
         * * `living_garden` - living_garden
         */
        categories?: Array<'beauty_wellness' | 'books_music' | 'car_mobility' | 'clothing_accessories' | 'grocery' | 'hobby_travel' | 'living_garden' | 'multimedia_electronics' | 'sports_lifestyle' | 'streaming_gaming' | 'toys_baby'>,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        voucherSelection?: boolean,
    }): CancelablePromise<PaginatedCadoozProductList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/cadooz-products/',
            query: {
                'categories': categories,
                'limit': limit,
                'offset': offset,
                'voucher_selection': voucherSelection,
            },
        });
    }

    /**
     * @returns CadoozProduct
     * @throws ApiError
     */
    public voucherCadoozProductsRetrieve({
        id,
    }: {
        /**
         * A unique value identifying this cadooz product.
         */
        id: string,
    }): CancelablePromise<CadoozProduct> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/cadooz-products/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns CadoozProduct
     * @throws ApiError
     */
    public voucherCadoozProductsSuggestionsRetrieve({
        order,
    }: {
        /**
         * The id of the EmployeeVoucherOrder
         */
        order: string,
    }): CancelablePromise<CadoozProduct> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/cadooz-products/suggestions/',
            query: {
                'order': order,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns PaginatedEmployeeVoucherOrderList
     * @throws ApiError
     */
    public voucherEmployeeOrdersList({
        accountingPeriodAfter,
        accountingPeriodBefore,
        company,
        limit,
        offset,
    }: {
        accountingPeriodAfter?: string,
        accountingPeriodBefore?: string,
        company?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedEmployeeVoucherOrderList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/employee-orders/',
            query: {
                'accounting_period_after': accountingPeriodAfter,
                'accounting_period_before': accountingPeriodBefore,
                'company': company,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns EmployeeVoucherOrderBulkCreate
     * @throws ApiError
     */
    public voucherEmployeeOrdersCreate({
        requestBody,
    }: {
        requestBody: EmployeeVoucherOrderBulkCreateRequest,
    }): CancelablePromise<EmployeeVoucherOrderBulkCreate> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/voucher/employee-orders/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns EmployeeVoucherOrder
     * @throws ApiError
     */
    public voucherEmployeeOrdersRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this employee voucher order.
         */
        id: string,
    }): CancelablePromise<EmployeeVoucherOrder> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/employee-orders/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns EmployeeVoucherOrderUpdate
     * @throws ApiError
     */
    public voucherEmployeeOrdersUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this employee voucher order.
         */
        id: string,
        requestBody: EmployeeVoucherOrderUpdateRequest,
    }): CancelablePromise<EmployeeVoucherOrderUpdate> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/voucher/employee-orders/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns EmployeeVoucherOrderUpdate
     * @throws ApiError
     */
    public voucherEmployeeOrdersPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this employee voucher order.
         */
        id: string,
        requestBody?: PatchedEmployeeVoucherOrderUpdateRequest,
    }): CancelablePromise<EmployeeVoucherOrderUpdate> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/voucher/employee-orders/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns void
     * @throws ApiError
     */
    public voucherEmployeeOrdersDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this employee voucher order.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/voucher/employee-orders/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns PaginatedCadoozVoucherList
     * @throws ApiError
     */
    public voucherVouchersList({
        accountingPeriodAfter,
        accountingPeriodBefore,
        company,
        employee,
        limit,
        offset,
    }: {
        accountingPeriodAfter?: string,
        accountingPeriodBefore?: string,
        company?: string,
        employee?: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedCadoozVoucherList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/vouchers/',
            query: {
                'accounting_period_after': accountingPeriodAfter,
                'accounting_period_before': accountingPeriodBefore,
                'company': company,
                'employee': employee,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns CadoozVoucher
     * @throws ApiError
     */
    public voucherVouchersRetrieve({
        id,
    }: {
        /**
         * A UUID string identifying this cadooz voucher.
         */
        id: string,
    }): CancelablePromise<CadoozVoucher> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/vouchers/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns CadoozVoucher
     * @throws ApiError
     */
    public voucherVouchersUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this cadooz voucher.
         */
        id: string,
        requestBody: CadoozVoucherRequest,
    }): CancelablePromise<CadoozVoucher> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/voucher/vouchers/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns CadoozVoucher
     * @throws ApiError
     */
    public voucherVouchersPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this cadooz voucher.
         */
        id: string,
        requestBody?: PatchedCadoozVoucherRequest,
    }): CancelablePromise<CadoozVoucher> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/voucher/vouchers/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Since almost all actions in the pxtra API must happen in the context of a specific company,
     * this mixin is provided to find out the targeted company and ensure the correct context.
     *
     * The target company is found in one of the following ways:
     * * If the current user is a company manager and provided the company param, this will determine
     * the target company. Lists require the param from managers acting through the dashboard
     * (platform token) and from managers without an employee record.
     * * If the current user is an employee, the target company is the employee's own company
     *
     *
     * After finding out the target company, the following measures are taken to ensure that only
     * the target company will be affected by any action:
     * * The viewset's queryset will be limited to objects associated with the target company
     * via the `filter_company_key`
     * * If the user is not associated with the target company, the permission will be denied
     * @returns CadoozVoucherArchiveInfoResponse
     * @throws ApiError
     */
    public voucherVouchersArchiveInfoRetrieve(): CancelablePromise<CadoozVoucherArchiveInfoResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/voucher/vouchers/archive/info/',
        });
    }

}
